import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import {
  finishExam,
  getCourse,
  getCourseVerification,
  getCourseViewById,
  getEnrolledUserCourseById,
  getSummary,
  getUnitView,
  getUnitViewTeacher,
  resetUnitExamTeacher,
  restartUnitExam,
  startUnitExam,
  startUnitExamTeacher,
} from '../../../api/course';
import { getLanguagesList } from '../../../api/dictionaries';
import { Bread, Breadcrumbs } from '../../../components/Breadcrumbs';
import { Loader } from '../../../components/Loader';
import SEO from '../../../components/SEO/SEO';
import { useNavigate } from '../../../hooks/useNavigate';
import { SelectOptions } from '../../../horizon-components/MMultSelect/MMultiSelect';
import {
  EduRoutes,
  MainRoutes,
} from '../../../horizon-layout/MainLayout/Routes/types/routes';
import {
  CompletedUnit,
  Course,
  TSummary,
  Unit,
  UnitItem,
} from '../../../models/Course';
import { useUserContext } from '../../../providers';
import { useTitleBackContext } from '../../../providers/TitleBackProvider/TitleBackProvider';
import { clearStringFromHtmlTags } from '../../../utils/clearStringFromHtmlTags';
import { TabsEnum } from '../course-view/ui/course-view-content';
import { CourseContentBody } from './components/CourseContentBody';
import { CourseExamBody } from './components/CourseExamBody';
import { CourseNavigation } from './components/CourseNavigation';
import { ExamResultInfo } from './components/ExamResultInfo';
import { PreviewWindow } from './components/PreviewWindow';
import { StaffVerification } from './components/StaffVerification';
import { SummaryResultInfo } from './components/SummaryResultInfo';
import { SummaryResultInfoOneUnit } from './components/SummaryResultInfoOneUnit';
import './EnrolledCoursePage.scss';

type Props = {
  isPreview?: boolean;
};

export const EnrolledCoursePage: React.FC<Props> = ({ isPreview }) => {
  const navigate = useNavigate();
  const { id: courseId } = useParams();
  const { updateWallet } = useUserContext();
  const { state }: { state: { isMyCourse: boolean; from: string } } = useLocation();
  const { t } = useTranslation(['translation']);
  const [isLoading, setIsLoading] = useState(true);
  const [languages, setLanguages] = useState<SelectOptions[]>([]);
  const [currentCourse, setCurrentCourse] = useState<Course | null>(null);
  const [enrolledCourse, setEnrolledCourse] = useState<Course | null>(null);
  const [currentUnit, setCurrentUnit] = useState<Unit | null>(null);
  const [currentUnitData, setCurrentUnitData] = useState<UnitItem | null>(null);
  const [currentUnitExamId, setCurrentUnitExamId] = useState<number | null>(null);
  const [isSummary, setIsSummary] = useState<boolean>(false);
  const [summary, setSummary] = useState<TSummary>();
  const [status, setStatus] = useState('');
  const { user } = useUserContext();
  const [breads, setBreads] = useState<Bread[]>([]);
  const [completedUnit, setCompletedUnit] = useState<CompletedUnit | null>(null);
  const { useTitleBack } = useTitleBackContext();
  const [isGradeCompleted, setIsGradeCompleted] = useState(false);
  const [isSubjectCompleted, setIsSubjectCompleted] = useState(false);
  const [isSubmitExam, setIsSubmitExam] = useState(false);

  useTitleBack(enrolledCourse?.title);

  useEffect(() => {
    if (enrolledCourse?.id) {
      setBreads([
        { title: 'EDU', url: MainRoutes.edu },
        {
          title: t('course.allCourses'),
          url: MainRoutes.edu + EduRoutes.courses,
        },
        {
          title: enrolledCourse?.subject?.title || t('course.Course'),
          url:
            MainRoutes.edu +
            EduRoutes.courses +
            `/?subject=${enrolledCourse?.subject?.slug}`,
        },
        {
          title: enrolledCourse?.title,
          url: MainRoutes.edu + EduRoutes.course + `/${courseId}`,
        },
      ]);
    }
  }, [courseId, enrolledCourse, t]);

  const isStaff = useMemo(
    () => !!user?.is_staff && !state?.isMyCourse,
    [state?.isMyCourse, user?.is_staff]
  );

  useEffect(() => {
    getLanguagesList().then((data) => {
      data?.results?.length &&
        setLanguages([
          ...data.results.map((item) => ({
            label: item.language_iso_code,
            value: item.id.toString(),
          })),
        ]);
    });
  }, []);

  useEffect(() => {
    if (!courseId) return;

    (async () => {
      setIsLoading(true);
      let enrolledCourse: Course;
      let currentCourse: Course;
      if (isPreview) {
        if (isStaff) {
          enrolledCourse = await getCourseViewById(Number(courseId));
          currentCourse = await getCourseVerification(courseId);
          setCurrentCourse(currentCourse);
        } else {
          enrolledCourse = await getCourseViewById(Number(courseId));
          currentCourse = await getCourse(courseId);
          setCurrentCourse(currentCourse);
        }
      } else {
        let course = await getEnrolledUserCourseById(Number(courseId));
        enrolledCourse = course.course;
        setStatus(course.status);
      }
      setEnrolledCourse(enrolledCourse);
      setCurrentUnit(enrolledCourse?.units?.[0]);
    })().finally(() => setIsLoading(false));
  }, [courseId, isPreview, isStaff]);

  useEffect(() => {
    if (!currentUnit) return;

    (async () => {
      setCurrentUnitData(null);
      let unitData: UnitItem;
      if (isPreview) {
        unitData = await getUnitViewTeacher(currentUnit.id);
      } else {
        unitData = await getUnitView(currentUnit.id);
      }
      setCurrentUnitData(unitData);
    })();
  }, [currentUnit, isPreview]);

  const handleResults = (unitData: UnitItem) => {
    unitData?.completed?.[0] && setCompletedUnit(unitData?.completed[0]);

    if (unitData?.started_exam && unitData?.completed?.length !== 0) {
      setCurrentUnitExamId(unitData?.started_exam);
    }

    if (enrolledCourse?.units.length === 1) {
      handleSummary();
    }
  };

  const leaveExam = () => {
    setCurrentUnitExamId(null);
  };

  const startExam = async (unitId: number) => {
    let examId;
    if (isPreview) {
      if (isStaff) {
        examId = Math.random();
      } else {
        try {
          const { id } = await startUnitExamTeacher(unitId);
          examId = id;
        } catch (e) {
          await resetUnitExamTeacher(unitId);
          const data = await startUnitExamTeacher(unitId);
          examId = data?.id;
        }
      }
    } else {
      if (!currentUnit) return;
      const unitData = await getUnitView(currentUnit.id);
      setCurrentUnitData(unitData);
      const foundUnit = enrolledCourse?.units?.find(
        (unit) => unit?.id === unitData?.id
      );
      if (foundUnit) {
        foundUnit.completed = unitData?.completed;
        setEnrolledCourse(enrolledCourse);
      }
      const startedExam = unitData?.started_exam;

      if (unitData?.completed?.length === 0 && !startedExam) {
        const { id } = await startUnitExam(unitId);
        examId = id;
      } else {
        examId = unitData?.started_exam;
        restartExam(currentUnit.id);
      }
    }
    setCurrentUnitExamId(examId);
  };

  const restartExam = async (unitId: number) => {
    const { id } = await restartUnitExam(unitId);
    setCurrentUnitExamId(id);
    setIsSummary(false);
    setCompletedUnit(null);
  };

  const isLastUnit = () => {
    if (!enrolledCourse || !currentUnit) return false;
    return (
      enrolledCourse.units[enrolledCourse.units.length - 1].id === currentUnit.id
    );
  };

  const nextUnit = () => {
    if (!enrolledCourse) return;
    const currentUnitIndex = enrolledCourse.units.findIndex(
      (unit) => unit.id === currentUnit?.id
    );
    if (currentUnitIndex === -1) return;

    const nextUnit = enrolledCourse.units[currentUnitIndex + 1];
    if (nextUnit) {
      setCurrentUnit(nextUnit);
      setCurrentUnitExamId(null);
      setCompletedUnit(null);
      setCurrentUnitExamId(null);
    }
  };

  const handleCourseEnd = () => {
    if (!enrolledCourse) return;

    const isMiscCourse = enrolledCourse?.subject.is_miscellaneous_subject;
    if (isMiscCourse || state?.from !== MainRoutes.start) {
      navigate(MainRoutes.edu + EduRoutes.courses);
    } else {
      navigate(state?.from || MainRoutes.start);
    }
  };

  const goToCourse = () =>
    navigate(
      `${MainRoutes.edu}${EduRoutes.course}/${courseId}?tab=${TabsEnum.FEEDBACK}`
    );

  const handleSummary = () => {
    getSummary(Number(courseId)).then(setSummary);
    setIsSummary(true);
  };

  const submitExam = async () => {
    if (currentUnitExamId && enrolledCourse) {
      setIsSubmitExam(true);
      const examResult = await finishExam(currentUnitExamId).finally(() =>
        setIsSubmitExam(false)
      );
      setCompletedUnit(examResult.completed_unit);
      setIsGradeCompleted(examResult.is_level_completed);
      setIsSubjectCompleted(examResult.is_subject_completed);

      const foundUnit = enrolledCourse?.units?.find(
        (unit) => unit?.id === currentUnitData?.id
      );
      if (foundUnit) {
        foundUnit.completed = [examResult.completed_unit];
        setEnrolledCourse({ ...enrolledCourse });
      }

      updateWallet();

      if (enrolledCourse.units.length === 1) {
        handleSummary();
      }
    }
  };

  useEffect(() => {
    !enrolledCourse?.id &&
      !isLoading &&
      navigate(`${MainRoutes.edu}${EduRoutes.course}/${courseId}`);
  }, [courseId, enrolledCourse?.id, navigate]);

  return (
    <div className="EnrolledCoursePage">
      <SEO
        title={enrolledCourse?.title}
        desc={clearStringFromHtmlTags(enrolledCourse?.description)}
        bannerUrl={enrolledCourse?.cover_image}
        loading={!enrolledCourse}
      />

      <Breadcrumbs breads={breads} className="hidden md:block" />
      {isLoading ? (
        <Loader />
      ) : (
        !!currentUnitData && (
          <div className="EnrolledCoursePage__content">
            <div className="EnrolledCoursePage__list">
              {/* Mobile Navigation */}
              {!!enrolledCourse && (
                <div className={'block md:hidden'}>
                  <CourseNavigation
                    course={enrolledCourse}
                    currentUnit={currentUnit}
                    setCurrentUnit={setCurrentUnit}
                    startExam={startExam}
                    currentUnitExam={currentUnitExamId}
                    leaveExam={leaveExam}
                  />
                </div>
              )}
              {/*Course content*/}
              <div className="EnrolledCoursePage__info content">
                {currentUnitData &&
                currentUnit &&
                !currentUnitExamId &&
                !isSummary ? (
                  <CourseContentBody
                    startExam={startExam}
                    currentUnit={currentUnit}
                    currentUnitData={currentUnitData}
                    languages={languages}
                    isStaff={isStaff}
                    isPreview={isPreview}
                    getResults={handleResults}
                  />
                ) : completedUnit && !isSummary ? (
                  <ExamResultInfo
                    completedUnit={completedUnit}
                    nextUnit={nextUnit}
                    isLastUnit={isLastUnit}
                    restartUnit={() => restartExam(currentUnitData.id)}
                    summaryCourse={handleSummary}
                    isGradeCompleted={isGradeCompleted}
                    isSubjectCompleted={isSubjectCompleted}
                    subjectTitle={enrolledCourse?.subject.title}
                    grade={enrolledCourse?.subject_level.title}
                  />
                ) : currentUnitExamId &&
                  currentUnitData &&
                  !!enrolledCourse &&
                  !isSummary ? (
                  <CourseExamBody
                    isStaff={isStaff}
                    isPreview={!!isPreview}
                    course={enrolledCourse}
                    currentUnitExamId={currentUnitExamId}
                    unitData={currentUnitData}
                    submitExam={submitExam}
                    nextUnit={nextUnit}
                    isLoading={isSubmitExam}
                  />
                ) : !!summary?.data?.length && isSummary ? (
                  enrolledCourse?.units.length === 1 ? (
                    <SummaryResultInfoOneUnit
                      summaryResult={summary}
                      rateCourse={goToCourse}
                      restartUnit={restartExam}
                      handleCourseEnd={handleCourseEnd}
                    />
                  ) : (
                    <SummaryResultInfo
                      summaryResult={summary}
                      rateCourse={goToCourse}
                      restartUnit={restartExam}
                    />
                  )
                ) : (
                  <Loader />
                )}
              </div>
            </div>
            {/* right section -navigation */}
            {!!enrolledCourse && (
              <div className="EnrolledCoursePage__aside md-max:!hidden">
                <CourseNavigation
                  course={enrolledCourse}
                  currentUnit={currentUnit}
                  setCurrentUnit={setCurrentUnit}
                  startExam={startExam}
                  currentUnitExam={currentUnitExamId}
                  leaveExam={leaveExam}
                  status={status}
                />
              </div>
            )}
          </div>
        )
      )}
      {isStaff && isPreview && !!courseId && (
        <StaffVerification courseId={courseId} />
      )}
      {isPreview && !isStaff && (
        <PreviewWindow courseId={courseId || ''} currentCourse={currentCourse} />
      )}
    </div>
  );
};
